import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  AccordionToggleEventHandler,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { useAtomValue } from "jotai";
import * as React from "react";
import { productAtom } from "@/state";
import { CitationCollection, DocumentCitation, PerformanceCitation, QaBankCitation } from "@/types";
import { CHAT_APP_URL } from "@/utils/constants";
import { fundReportUrl } from "@/utils/card-utils";

const { useMemo } = React;

type Props = CitationCollection;

type CitationItem =
  | { type: "document"; citation: DocumentCitation; index: number }
  | { type: "qaBank"; citation: QaBankCitation; index: number }
  | { type: "performance"; citation: PerformanceCitation; index: number };

export default function CitationsList({ documentCitations, qaBankCitations, performanceCitations = [] }: Props) {
  const [openItems, setOpenItems] = React.useState<string[]>([]);

  const handleToggle: AccordionToggleEventHandler<string> = (_, data) => {
    setOpenItems(data.openItems);
  };

  const allCitations = useMemo(
    () => createCitationItems(documentCitations, qaBankCitations, performanceCitations),
    [documentCitations, qaBankCitations, performanceCitations]
  );

  if (allCitations.length === 0) {
    return null;
  }

  return (
    <Accordion openItems={openItems} onToggle={handleToggle} multiple collapsible>
      {allCitations.map((item: CitationItem) => (
        <CitationItemRenderer key={item.citation.id} item={item} />
      ))}
    </Accordion>
  );
}

// Numbering matches enhanceAnswerWithCitations: documents, answer bank, then fund reporting.
const createCitationItems = (
  documentCitations: DocumentCitation[],
  qaBankCitations: QaBankCitation[],
  performanceCitations: PerformanceCitation[]
): CitationItem[] => [
  ...documentCitations.map((citation, index) => ({
    type: "document" as const,
    citation,
    index,
  })),
  ...qaBankCitations.map((citation, index) => ({
    type: "qaBank" as const,
    citation,
    index: documentCitations.length + index,
  })),
  ...performanceCitations.map((citation, index) => ({
    type: "performance" as const,
    citation,
    index: documentCitations.length + qaBankCitations.length + index,
  })),
];

const CitationItemRenderer: React.FC<{ item: CitationItem }> = ({ item }) => {
  switch (item.type) {
    case "document":
      return <DocumentCitationItem citation={item.citation} index={item.index} />;
    case "qaBank":
      return <QaBankCitationItem citation={item.citation} index={item.index} />;
    case "performance":
      return <PerformanceCitationItem citation={item.citation} index={item.index} />;
  }
};

const DocumentCitationItem: React.FC<{ citation: DocumentCitation; index: number }> = ({
  citation,
  index
}) => {
  const styles = useStyles();

  return (
    <AccordionItem value={`${index}`}>
      <AccordionHeader>
        <div className={styles.header__container}>
          <div className={styles.header__text}>
            <span className={styles.header__index}>[{index + 1}]</span>
            <span className={styles.header__filename}>{citation.filename}</span>
          </div>
        </div>
      </AccordionHeader>
      <AccordionPanel>
        <div className={styles.panel__content}>{citation.quote}</div>
      </AccordionPanel>
    </AccordionItem>
  );
};

const QaBankCitationItem: React.FC<{ citation: QaBankCitation; index: number }> = ({
  citation,
  index
}) => {
  const styles = useStyles();
  const deepLink = citation.qaBankQuestionId
    ? `${CHAT_APP_URL}/qa-bank?questionId=${citation.qaBankQuestionId}`
    : null;

  return (
    <AccordionItem value={`${index}`}>
      <AccordionHeader>
        <div className={styles.header__container}>
          <div className={styles.header__text}>
            <span className={styles.header__index}>[{index + 1}]</span>
            {deepLink ? (
              <a href={deepLink} target="_blank" rel="noopener noreferrer" className={styles.header__link}>
                Answer Bank
              </a>
            ) : (
              <span className={styles.header__filename}>Answer Bank</span>
            )}
          </div>
        </div>
      </AccordionHeader>
      <AccordionPanel>
        <div className={styles.panel__content}>
          <div className={styles.qa_question}>{citation.question}</div>
          <div className={styles.qa_answer}>{citation.answer}</div>
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
};

const PerformanceCitationItem: React.FC<{ citation: PerformanceCitation; index: number }> = ({ citation, index }) => {
  const styles = useStyles();
  const product = useAtomValue(productAtom);
  const deepLink = product?.id ? fundReportUrl(product.id, citation) : null;
  const { ref } = citation;
  const provenance = ref.sheet && ref.cell ? `${ref.sheet}!${ref.cell}` : ref.cell;

  return (
    <AccordionItem value={`${index}`}>
      <AccordionHeader>
        <div className={styles.header__container}>
          <div className={styles.header__text}>
            <span className={styles.header__index}>[{index + 1}]</span>
            {deepLink ? (
              <a href={deepLink} target="_blank" rel="noopener noreferrer" className={styles.header__link}>
                Fund Reporting
              </a>
            ) : (
              <span className={styles.header__filename}>Fund Reporting</span>
            )}
            <span className={styles.header__filename}>
              {ref.fundName} · {ref.period}
            </span>
          </div>
        </div>
      </AccordionHeader>
      <AccordionPanel>
        <div className={styles.panel__content}>
          <div className={styles.qa_answer}>{citation.quote}</div>
          {provenance && <div className={styles.provenance}>{provenance}</div>}
          {citation.reportDeleted && <div className={styles.provenance}>This fund report is no longer available.</div>}
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
};

const useStyles = makeStyles({
  header__container: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },

  header__text: {
    fontSize: "12px",
    fontWeight: "semibold",
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  header__index: {
    color: tokens.colorPaletteLavenderForeground2,
  },

  header__filename: {
    fontStyle: "italic",
    color: tokens.colorNeutralForeground2,
  },

  header__link: {
    fontStyle: "italic",
    color: tokens.colorPaletteLavenderForeground2,
    textDecoration: "none",
    ":hover": {
      textDecoration: "underline",
    },
  },

  panel__content: {
    padding: "8px",
    backgroundColor: tokens.colorNeutralBackground4,
    fontSize: "11px",
    fontStyle: "italic",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordWrap: "break-word",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    hyphens: "auto",
    whiteSpace: "pre-wrap",
  },

  qa_question: {
    fontWeight: "bold",
    marginBottom: "8px",
  },

  qa_answer: {
    fontStyle: "normal",
  },

  provenance: {
    marginTop: "6px",
    fontSize: "10px",
    color: tokens.colorNeutralForeground3,
  },
});
